import React from "react";
import { observer } from "mobx-react-lite";
import dayjs from "dayjs";
import { Tooltip } from "antd";
import type { ScheduleStore } from "../store";
import type { ScheduleItem, ScheduleLocation } from "../../FestivalCalcPage/types";

// July in Berlin = CEST (UTC+2), so noon local = 10:00 UTC
const FESTIVAL_START = dayjs("2026-09-18T12:00:00Z");
const TOTAL_MINUTES = 48 * 60;

const TYPE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  general:   { bg: "#f0f0f0", border: "#bfbfbf", text: "#595959" },
  workshop:  { bg: "#e6f4ff", border: "#1677ff", text: "#003eb3" },
  music_act: { bg: "#f9f0ff", border: "#722ed1", text: "#391085" },
};

const TYPE_LABELS: Record<string, string> = {
  general: "Allgemein",
  workshop: "Workshop",
  music_act: "Musikact",
};

function offsetPercent(startTime: Date): number {
  const diffMinutes = dayjs(startTime).diff(FESTIVAL_START, "minute");
  return Math.max(0, (diffMinutes / TOTAL_MINUTES) * 100);
}

function widthPercent(durationMinutes: number): number {
  return Math.max(0.5, (durationMinutes / TOTAL_MINUTES) * 100);
}

interface ItemBlockProps {
  item: ScheduleItem;
  onClick: () => void;
}

const ItemBlock = ({ item, onClick }: ItemBlockProps) => {
  const colors = TYPE_COLORS[item.type] ?? TYPE_COLORS.general;
  const left = offsetPercent(item.startTime);
  const width = widthPercent(item.durationMinutes);
  const endTime = dayjs(item.startTime).add(item.durationMinutes, "minute");
  const tooltipTitle = `${dayjs(item.startTime).format("HH:mm")} – ${endTime.format("HH:mm")} | ${TYPE_LABELS[item.type]}${item.capacity ? ` | ${item.capacity} Pers.` : ""}`;

  return (
    <Tooltip title={tooltipTitle}>
      <div
        onClick={onClick}
        style={{
          position: "absolute",
          left: `${left}%`,
          width: `${width}%`,
          top: 4,
          bottom: 4,
          background: colors.bg,
          border: `1.5px solid ${colors.border}`,
          borderRadius: 4,
          padding: "2px 6px",
          overflow: "hidden",
          cursor: "pointer",
          boxSizing: "border-box",
          minWidth: 20,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.title || "(kein Titel)"}
        </div>
        <div style={{ fontSize: 11, color: colors.text, opacity: 0.8 }}>
          {dayjs(item.startTime).format("HH:mm")}
        </div>
      </div>
    </Tooltip>
  );
};

function buildHourLabels(): { label: string; offset: number }[] {
  const labels = [];
  for (let h = 0; h < 48; h++) {
    const t = FESTIVAL_START.add(h, "hour");
    labels.push({
      label: t.format("HH:mm"),
      offset: (h / 48) * 100,
    });
  }
  return labels;
}

function buildDayLabels(): { label: string; offset: number }[] {
  const days = [];
  for (let h = 0; h < 48; h += 24) {
    const t = FESTIVAL_START.add(h, "hour");
    days.push({ label: t.format("dd DD.MM."), offset: (h / 48) * 100 });
  }
  return days;
}

interface Props {
  store: ScheduleStore;
  locations: ScheduleLocation[];
}

export const ScheduleTimeline = observer(({ store, locations }: Props) => {
  const hourLabels = buildHourLabels();
  const dayLabels = buildDayLabels();

  const ROW_HEIGHT = 56;
  const LABEL_WIDTH = 140;
  const HEADER_HEIGHT = 48;

  const containerStyle: React.CSSProperties = {
    overflowX: "auto",
    fontFamily: "inherit",
  };

  const gridStyle: React.CSSProperties = {
    minWidth: 2800,
    position: "relative",
  };

  return (
    <div style={containerStyle}>
      <div style={gridStyle}>
        {/* Day header */}
        <div style={{ display: "flex", height: HEADER_HEIGHT / 2, borderBottom: "1px solid #f0f0f0" }}>
          <div style={{ width: LABEL_WIDTH, flexShrink: 0 }} />
          <div style={{ flex: 1, position: "relative" }}>
            {dayLabels.map((d,i) => (
              <div
                key={`${d.label}-${i}`}
                style={{
                  position: "absolute",
                  left: `${d.offset}%`,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#262626",
                  paddingTop: 4,
                  paddingLeft: 4,
                }}
              >
                {d.label}
              </div>
            ))}
          </div>
        </div>

        {/* Hour header */}
        <div style={{ display: "flex", height: HEADER_HEIGHT / 2, borderBottom: "2px solid #d9d9d9" }}>
          <div style={{ width: LABEL_WIDTH, flexShrink: 0 }} />
          <div style={{ flex: 1, position: "relative" }}>
            {hourLabels.map((h,i) => (
              <div
                key={`${h.label}-${i}`}
                style={{
                  position: "absolute",
                  left: `${h.offset}%`,
                  fontSize: 10,
                  color: "#8c8c8c",
                  paddingTop: 2,
                  transform: "translateX(-50%)",
                  whiteSpace: "nowrap",
                }}
              >
                {h.label}
              </div>
            ))}
            {/* hour grid lines */}
            {hourLabels.map((h,i) => (
              <div
                key={`line-${h.label}-${i}`}
                style={{
                  position: "absolute",
                  left: `${h.offset}%`,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: "#f0f0f0",
                }}
              />
            ))}
          </div>
        </div>

        {/* Location rows */}
        {locations.length === 0 && (
          <div style={{ padding: "32px 16px", color: "#8c8c8c", textAlign: "center" }}>
            Noch keine Orte konfiguriert. Bitte in der Konfiguration unter SCH_FestivalConfig hinzufügen.
          </div>
        )}

        {locations.map((loc, rowIdx) => {
          const rowItems = store.items.filter((i) => i.locationId === loc.id);
          return (
            <div
              key={loc.id}
              style={{
                display: "flex",
                height: ROW_HEIGHT,
                borderBottom: "1px solid #f0f0f0",
                background: rowIdx % 2 === 0 ? "#fff" : "#fafafa",
              }}
            >
              <div
                style={{
                  width: LABEL_WIDTH,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: 12,
                  paddingRight: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#262626",
                  borderRight: "1px solid #f0f0f0",
                }}
              >
                {loc.name}
              </div>
              <div style={{ flex: 1, position: "relative" }}>
                {/* vertical hour lines */}
                {hourLabels.map((h,i) => (
                  <div
                    key={`rl-${h.label}-${i}`}
                    style={{
                      position: "absolute",
                      left: `${h.offset}%`,
                      top: 0,
                      bottom: 0,
                      width: 1,
                      background: "#f5f5f5",
                      pointerEvents: "none",
                    }}
                  />
                ))}
                {rowItems.map((item) => (
                  <ItemBlock
                    key={item.objectId}
                    item={item}
                    onClick={() => store.selectItem(item)}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* Items without a matching location */}
        {store.items.filter((i) => !locations.find((l) => l.id === i.locationId)).length > 0 && (
          <div
            style={{
              display: "flex",
              height: ROW_HEIGHT,
              borderBottom: "1px solid #f0f0f0",
              background: "#fffbe6",
            }}
          >
            <div
              style={{
                width: LABEL_WIDTH,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                paddingLeft: 12,
                fontSize: 13,
                color: "#d48806",
                borderRight: "1px solid #f0f0f0",
              }}
            >
              (unbekannter Ort)
            </div>
            <div style={{ flex: 1, position: "relative" }}>
              {store.items
                .filter((i) => !locations.find((l) => l.id === i.locationId))
                .map((item) => (
                  <ItemBlock
                    key={item.objectId}
                    item={item}
                    onClick={() => store.selectItem(item)}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
